import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import {
  Gift, Star, Users, TrendingUp, Award, ChevronLeft, ChevronRight,
  Search, RefreshCw, Phone, ShoppingCart, Crown, Zap, Plus, Trash2, Send, X
} from 'lucide-react';

const LoyaltyAdminPage = () => {
  const { api } = useAuth();
  const { t, formatCurrency, formatDate } = useLanguage();
  // Les niveaux sont des valeurs backend (Bronze/Argent/Or) : on ne traduit que l'affichage, repli sur la valeur brute.
  const LEVEL_LABEL_KEYS = { Bronze: 'loyaltyAdmin.levelBronze', Argent: 'loyaltyAdmin.levelArgent', Or: 'loyaltyAdmin.levelOr' };
  const levelLabel = (lvl) => (LEVEL_LABEL_KEYS[lvl] ? t(LEVEL_LABEL_KEYS[lvl]) : lvl);
  const [overview, setOverview] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [flashOffers, setFlashOffers] = useState([]);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerForm, setOfferForm] = useState({ title: '', description: '', discount_percent: 10, end_date: '', target_levels: ['Or', 'Argent'] });
  const [offerLoading, setOfferLoading] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await api.get('/loyalty/admin/overview');
      setOverview(res.data);
    } catch (err) { console.error(err); }
  }, [api]);

  const fetchMembers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      const res = await api.get(`/loyalty/admin/members?${params}`);
      setMembers(res.data.members || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
  }, [api, page, search]);

  const fetchFlashOffers = useCallback(async () => {
    try {
      const res = await api.get('/flash-offers');
      setFlashOffers(res.data || []);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchOverview(), fetchMembers(), fetchFlashOffers()]);
      setLoading(false);
    })();
  }, [fetchOverview, fetchMembers, fetchFlashOffers]);

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchOverview(), fetchMembers(), fetchFlashOffers()]);
    setLoading(false);
  };

  const handleCreateOffer = async () => {
    if (!offerForm.title.trim()) return;
    setOfferLoading(true);
    try {
      await api.post('/flash-offers', offerForm);
      setShowOfferForm(false);
      setOfferForm({ title: '', description: '', discount_percent: 10, end_date: '', target_levels: ['Or', 'Argent'] });
      await fetchFlashOffers();
    } catch (err) { console.error(err); }
    setOfferLoading(false);
  };

  const handleDeleteOffer = async (id) => {
    if (!window.confirm(t('loyaltyAdmin.confirmDeleteOffer'))) return;
    try {
      await api.delete(`/flash-offers/${id}`);
      await fetchFlashOffers();
    } catch (err) { console.error(err); }
  };

  const handleToggleOffer = async (offer) => {
    try {
      await api.put(`/flash-offers/${offer.id}`, { active: !offer.active });
      await fetchFlashOffers();
    } catch (err) { console.error(err); }
  };

  const handleNotifyOffer = async (id) => {
    try {
      const res = await api.post(`/flash-offers/${id}/notify`);
      alert(res.data.message || t('loyaltyAdmin.emailsSent'));
      await fetchFlashOffers();
    } catch (err) { console.error(err); }
  };

  if (loading && !overview) return (
    <Layout>
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="loyalty-admin-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" />
              </div>
              {t('loyaltyAdmin.title')}
            </h1>
            <p className="text-sm text-gray-400 mt-1">{t('loyaltyAdmin.subtitle')}</p>
          </div>
          <button onClick={handleRefresh} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-sm flex items-center gap-2 text-gray-300" data-testid="loyalty-refresh-btn">
            <RefreshCw className="w-4 h-4" /> {t('loyaltyAdmin.refresh')}
          </button>
        </div>

        {/* KPIs */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass p-4 rounded-xl border border-amber-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-amber-400" />
                <span className="text-xs text-gray-400">{t('loyaltyAdmin.kpiMembers')}</span>
              </div>
              <div className="text-3xl font-black text-amber-400" data-testid="loyalty-total-members">{overview.total_members}</div>
            </div>
            <div className="glass p-4 rounded-xl border border-emerald-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-emerald-400" />
                <span className="text-xs text-gray-400">{t('loyaltyAdmin.kpiPendingRewards')}</span>
              </div>
              <div className="text-3xl font-black text-emerald-400" data-testid="loyalty-pending-rewards">{overview.with_pending_rewards}</div>
            </div>
            <div className="glass p-4 rounded-xl border border-cyan-500/20">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-cyan-400" />
                <span className="text-xs text-gray-400">{t('loyaltyAdmin.kpiRedeemed')}</span>
              </div>
              <div className="text-3xl font-black text-cyan-400">{overview.total_redeemed}</div>
            </div>
            <div className="glass p-4 rounded-xl border border-purple-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-purple-400" />
                <span className="text-xs text-gray-400">{t('loyaltyAdmin.kpiThreshold')}</span>
              </div>
              <div className="text-3xl font-black text-purple-400">{overview.purchases_for_reward} <span className="text-sm font-normal text-gray-400">{t('loyaltyAdmin.purchasesSuffix')}</span></div>
              <div className="text-xs text-gray-500 mt-1">{t('loyaltyAdmin.rewardRule', { pos: overview.pos_reward_percent, repair: overview.repair_reward_percent })}</div>
            </div>
          </div>
        )}

        {/* Top Clients */}
        {overview?.top_clients?.length > 0 && (
          <div className="glass p-4 rounded-xl">
            <h3 className="font-bold text-gray-200 mb-3 flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" /> {t('loyaltyAdmin.topClients')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {overview.top_clients.slice(0, 5).map((c, i) => (
                <div key={c.client_tel} className={`p-3 rounded-xl border ${i === 0 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-white/5 border-white/10'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold ${i === 0 ? 'text-amber-400' : 'text-gray-400'}`}>#{i + 1}</span>
                    <span className="text-sm font-semibold text-white truncate">{c.client_nom || t('loyaltyAdmin.clientFallback')}</span>
                  </div>
                  <div className="text-xs text-gray-400 flex items-center gap-1"><Phone className="w-3 h-3" />{c.client_tel}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500"><ShoppingCart className="w-3 h-3 inline mr-1" />{c.total_purchases} {t('loyaltyAdmin.purchasesSuffix')}</span>
                    <span className="text-xs font-bold text-cyan-400">{c.current_points}/10</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Flash Offers Section */}
        <div className="glass p-4 rounded-xl" data-testid="flash-offers-admin">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-200 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" /> {t('loyaltyAdmin.flashOffers')}
            </h3>
            <button onClick={() => setShowOfferForm(true)} className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-400 text-xs font-semibold flex items-center gap-1 hover:bg-amber-500/30 transition-colors" data-testid="create-flash-offer-btn">
              <Plus className="w-3.5 h-3.5" /> {t('loyaltyAdmin.newOffer')}
            </button>
          </div>

          {/* Create Offer Form */}
          {showOfferForm && (
            <div className="mb-4 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5 space-y-3" data-testid="flash-offer-form">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-amber-400">{t('loyaltyAdmin.createFlashOffer')}</span>
                <button onClick={() => setShowOfferForm(false)} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <input
                type="text"
                value={offerForm.title}
                onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })}
                placeholder={t('loyaltyAdmin.offerTitlePlaceholder')}
                className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-amber-500/50"
                data-testid="offer-title-input"
              />
              <input
                type="text"
                value={offerForm.description}
                onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })}
                placeholder={t('loyaltyAdmin.offerDescPlaceholder')}
                className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-amber-500/50"
                data-testid="offer-desc-input"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">{t('loyaltyAdmin.discountLabel')}</label>
                  <input
                    type="number"
                    value={offerForm.discount_percent}
                    onChange={(e) => setOfferForm({ ...offerForm, discount_percent: parseInt(e.target.value) || 0 })}
                    min="1" max="90"
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-500/50"
                    data-testid="offer-discount-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">{t('loyaltyAdmin.endDateLabel')}</label>
                  <input
                    type="datetime-local"
                    value={offerForm.end_date ? offerForm.end_date.slice(0, 16) : ''}
                    onChange={(e) => setOfferForm({ ...offerForm, end_date: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-500/50"
                    data-testid="offer-end-date-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">{t('loyaltyAdmin.targetLevelsLabel')}</label>
                <div className="flex gap-2">
                  {['Bronze', 'Argent', 'Or'].map(level => (
                    <button key={level} type="button"
                      onClick={() => {
                        const levels = offerForm.target_levels.includes(level)
                          ? offerForm.target_levels.filter(l => l !== level)
                          : [...offerForm.target_levels, level];
                        setOfferForm({ ...offerForm, target_levels: levels });
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        offerForm.target_levels.includes(level)
                          ? 'bg-amber-500/30 text-amber-400 border border-amber-500/40'
                          : 'bg-white/5 text-gray-500 border border-white/10'
                      }`}
                      data-testid={`offer-level-${level.toLowerCase()}`}
                    >
                      {levelLabel(level)}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={handleCreateOffer} disabled={offerLoading || !offerForm.title.trim()}
                className="w-full py-2 rounded-lg bg-amber-500 text-black font-semibold text-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
                data-testid="submit-flash-offer-btn">
                {offerLoading ? t('loyaltyAdmin.creating') : t('loyaltyAdmin.createOffer')}
              </button>
            </div>
          )}

          {/* Offers List */}
          {flashOffers.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">{t('loyaltyAdmin.noOffers')}</p>
          ) : (
            <div className="space-y-2">
              {flashOffers.map((o) => (
                <div key={o.id} className={`flex items-center gap-3 p-3 rounded-xl border ${o.active ? 'border-amber-500/20 bg-amber-500/5' : 'border-white/5 bg-white/[.02] opacity-60'}`} data-testid={`flash-offer-item-${o.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
                    <span className="text-amber-400 font-black text-sm">-{o.discount_percent}%</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{o.title}</div>
                    <div className="text-xs text-gray-500 truncate">{o.description}</div>
                    <div className="text-[10px] text-gray-600 mt-0.5">
                      {t('loyaltyAdmin.offerMeta', { levels: (o.target_levels || []).map(levelLabel).join(', '), emails: o.emails_sent || 0 })}
                      {o.end_date && t('loyaltyAdmin.offerEnd', { date: formatDate(o.end_date) })}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => handleToggleOffer(o)} className={`p-1.5 rounded-lg text-xs ${o.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-gray-500'}`} title={o.active ? t('loyaltyAdmin.deactivate') : t('loyaltyAdmin.activate')} data-testid={`toggle-offer-${o.id}`}>
                      {o.active ? 'ON' : 'OFF'}
                    </button>
                    <button onClick={() => handleNotifyOffer(o.id)} className="p-1.5 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30" title={t('loyaltyAdmin.sendByEmail')} data-testid={`notify-offer-${o.id}`}>
                      <Send className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDeleteOffer(o.id)} className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30" title={t('loyaltyAdmin.delete')} data-testid={`delete-offer-${o.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Members Table */}
        <div className="glass p-4 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-200 flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" /> {t('loyaltyAdmin.allMembers', { count: total })}
            </h3>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder={t('loyaltyAdmin.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/10 border border-white/10 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-cyan-500/50"
                data-testid="loyalty-search"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-dark-custom text-sm" data-testid="loyalty-members-table">
              <thead>
                <tr>
                  <th>{t('loyaltyAdmin.colClient')}</th>
                  <th>{t('loyaltyAdmin.colPhone')}</th>
                  <th>{t('loyaltyAdmin.colPurchases')}</th>
                  <th>{t('loyaltyAdmin.colPoints')}</th>
                  <th>{t('loyaltyAdmin.colTotalSpent')}</th>
                  <th>{t('loyaltyAdmin.colStatus')}</th>
                  <th>{t('loyaltyAdmin.colRewards')}</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-8 text-gray-400">{t('loyaltyAdmin.noMembers')}</td></tr>
                ) : members.map((m, i) => (
                  <tr key={m.client_tel}>
                    <td className="font-semibold">{m.client_nom || '-'}</td>
                    <td className="font-mono text-cyan-400">{m.client_tel}</td>
                    <td className="text-center font-bold">{m.total_purchases}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-amber-400">{m.current_points}/10</span>
                        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden max-w-[60px]">
                          <div className="h-full bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full" style={{ width: `${(m.current_points / 10) * 100}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="text-right">{formatCurrency(m.total_spent || 0)}</td>
                    <td>
                      {m.pending_reward ? (
                        <span className="px-2 py-1 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400">{t('loyaltyAdmin.rewardAvailable')}</span>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs bg-white/10 text-gray-400">{t('loyaltyAdmin.purchasesRemaining', { count: 10 - m.current_points })}</span>
                      )}
                    </td>
                    <td className="text-center font-bold text-purple-400">{(m.rewards_history || []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 py-3 mt-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronLeft className="w-5 h-5" /></button>
              <span className="text-sm text-gray-400">{t('loyaltyAdmin.pageOf', { page, total: totalPages })}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronRight className="w-5 h-5" /></button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default LoyaltyAdminPage;
